import { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { Colors } from '../../../../../assets';
import { Spacing } from '../../../../../assets/GlobalStyle/Variables';
import { RequestsFormValues } from '../../models';
import { FieldError } from './FieldError';

const p = 'microbiology.form_data' as const;

export const MicrobiologyForm: FC = () => {
  const {
    register,
    watch,
    formState: { errors }
  } = useFormContext<RequestsFormValues>();

  const e = (errors as any)?.microbiology;

  const pusPusEnabled = watch('microbiology.form_data.pus_fluid_tissue.pus.enabled');
  const pusAbscessEnabled = watch('microbiology.form_data.pus_fluid_tissue.abscess_cyst.enabled');
  const pusEffusionEnabled = watch('microbiology.form_data.pus_fluid_tissue.effusion.enabled');
  const pusBiopsyEnabled = watch('microbiology.form_data.pus_fluid_tissue.biopsy_tissue.enabled');

  const dcdOtherEnabled = watch('microbiology.form_data.dcd.other.enabled');
  const earOtherEnabled = watch('microbiology.form_data.ear.other.enabled');
  const eyeOtherEnabled = watch('microbiology.form_data.eye.other.enabled');
  const neoOtherEnabled = watch('microbiology.form_data.neonatology.other.enabled');
  const pathOtherEnabled = watch('microbiology.form_data.pathology.other.enabled');
  const sterOtherEnabled = watch('microbiology.form_data.sterility.other.enabled');
  const tbcEnabled = watch('microbiology.form_data.tbc_cultivation.enabled');
  const bactecSource = watch('microbiology.form_data.bloodstream_bactec.collection_source');
  const bactecOtherEnabled = watch(
    'microbiology.form_data.bloodstream_bactec.other_request.enabled'
  );
  const mycoEnabled = watch('microbiology.form_data.mycology.cultivation.enabled');

  return (
    <Wrapper>
      <MainGrid>
        {/* ============ COLUMN 1 ============ */}
        <Column>
          <SectionBlock>
            <SectionTitle>Hnis / tekutina / tkáň</SectionTitle>
            <Items>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.pus_fluid_tissue.pus.enabled`)} />
                <span>Hnis — lokalizace:</span>
              </CheckRow>
              {pusPusEnabled && (
                <SubInput
                  placeholder="Lokalizace"
                  {...register(`${p}.pus_fluid_tissue.pus.localization`)}
                />
              )}
              <CheckRow>
                <input
                  type="checkbox"
                  {...register(`${p}.pus_fluid_tissue.abscess_cyst.enabled`)}
                />
                <span>Absces / cysta — lokalizace:</span>
              </CheckRow>
              {pusAbscessEnabled && (
                <SubInput
                  placeholder="Lokalizace"
                  {...register(`${p}.pus_fluid_tissue.abscess_cyst.localization`)}
                />
              )}
              <CheckRow>
                <input type="checkbox" {...register(`${p}.pus_fluid_tissue.furuncle_carbuncle`)} />
                <span>Furunkl / karbunkl</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.pus_fluid_tissue.effusion.enabled`)} />
                <span>Výpotek — lokalizace:</span>
              </CheckRow>
              {pusEffusionEnabled && (
                <SubInput
                  placeholder="Lokalizace"
                  {...register(`${p}.pus_fluid_tissue.effusion.localization`)}
                />
              )}
              <CheckRow>
                <input
                  type="checkbox"
                  {...register(`${p}.pus_fluid_tissue.biopsy_tissue.enabled`)}
                />
                <span>Biopsie / tkáň — lokalizace:</span>
              </CheckRow>
              {pusBiopsyEnabled && (
                <SubInput
                  placeholder="Lokalizace"
                  {...register(`${p}.pus_fluid_tissue.biopsy_tissue.localization`)}
                />
              )}
              <CheckRow>
                <input type="checkbox" {...register(`${p}.pus_fluid_tissue.joint_puncture`)} />
                <span>Kloubní punktát</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.pus_fluid_tissue.ascites`)} />
                <span>Ascites</span>
              </CheckRow>
            </Items>
          </SectionBlock>

          <SectionBlock>
            <SectionTitle>GIT</SectionTitle>
            <Items>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.git.rectal_swab_enteric_pathogens`)} />
                <span>Rektální stěr — střevní patogeny</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.git.cholera_swab`)} />
                <span>Rektální stěr — cholera</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.git.rectal_swab_esbl_kpc_vre`)} />
                <span>Rektální stěr — ESBL/KPC/VRE</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.git.bile`)} />
                <span>Žluč</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.git.appendix_swab`)} />
                <span>Stěr appendix</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.git.clostridioides_difficile`)} />
                <span>Clostridioides difficile</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.git.stool_viruses`)} />
                <span>Stolice — viry</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.git.helicobacter_pylori`)} />
                <span>Helicobacter pylori</span>
              </CheckRow>
            </Items>
          </SectionBlock>

          <SectionBlock>
            <SectionTitle>HCD (horní cesty dýchací)</SectionTitle>
            <SelectRow>
              <SelectLabel>Typ kultivace:</SelectLabel>
              <StyledSelect {...register(`${p}.hcd.cultivation_type`)}>
                <option value="">— nevybráno —</option>
                <option value="general">Obecná</option>
                <option value="diphtheria">Záškrt</option>
                <option value="bordetella">Bordetella</option>
              </StyledSelect>
            </SelectRow>
            <Items>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.hcd.throat_tonsils_nasopharynx`)} />
                <span>Krk / mandle / nosohltanu</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.hcd.nose`)} />
                <span>Nos</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.hcd.oral_mucosa_tongue_swab`)} />
                <span>Ústní sliznice / jazyk</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.hcd.paranasal_sinuses`)} />
                <span>Vedlejší nosní dutiny</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.hcd.peritonsillar_abscess`)} />
                <span>Peritonzilární absces</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.hcd.sars_cov2_antigen_nasopharynx`)} />
                <span>SARS-CoV-2 antigen — nasohltanový výtěr</span>
              </CheckRow>
            </Items>
          </SectionBlock>

          <SectionBlock>
            <SectionTitle>DCD (dolní cesty dýchací)</SectionTitle>
            <SelectRow>
              <SelectLabel>Typ kultivace:</SelectLabel>
              <StyledSelect {...register(`${p}.dcd.cultivation_type`)}>
                <option value="">— nevybráno —</option>
                <option value="general">Obecná</option>
                <option value="legionella">Legionella</option>
                <option value="bordetella">Bordetella</option>
              </StyledSelect>
            </SelectRow>
            <Items>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.dcd.sputum`)} />
                <span>Sputum</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.dcd.bal_aspirate`)} />
                <span>BAL / aspirát</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.dcd.tracheostomy_swab`)} />
                <span>Tracheostomický stěr</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.dcd.thoracic_puncture`)} />
                <span>Hrudní punktát</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.dcd.legionella_antigen_urine`)} />
                <span>Legionella antigen — moč</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.dcd.pneumococcus_antigen_urine`)} />
                <span>Pneumokok antigen — moč</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.dcd.other.enabled`)} />
                <span>Jiné</span>
              </CheckRow>
              {dcdOtherEnabled && (
                <>
                  <SubInput
                    placeholder="Upřesnění"
                    {...register(`${p}.dcd.other.text`, {
                      validate: (v) => !dcdOtherEnabled || !!v?.trim() || 'Upřesnění je povinné'
                    })}
                  />
                  <FieldError error={e?.form_data?.dcd?.other?.text} />
                </>
              )}
            </Items>
          </SectionBlock>
        </Column>

        {/* ============ COLUMN 2 ============ */}
        <Column>
          <SectionBlock>
            <SectionTitle>Urogenitální / STI</SectionTitle>
            <SelectRow>
              <SelectLabel>Typ kultivace:</SelectLabel>
              <StyledSelect {...register(`${p}.urogenital_sti.cultivation_type`)}>
                <option value="">— nevybráno —</option>
                <option value="general">Obecná</option>
                <option value="go">Kapavka (GO)</option>
                <option value="gbs">GBS</option>
              </StyledSelect>
            </SelectRow>
            <Items>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.urogenital_sti.vaginal_swab`)} />
                <span>Vaginální stěr</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.urogenital_sti.rectal_swab`)} />
                <span>Rektální stěr</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.urogenital_sti.urethral_swab`)} />
                <span>Uretrální stěr</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.urogenital_sti.throat_swab`)} />
                <span>Krční stěr</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.urogenital_sti.cervical_swab`)} />
                <span>Cervikální stěr</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.urogenital_sti.prostatic_secretion`)} />
                <span>Prostatický sekret</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.urogenital_sti.vaginorectal_swab`)} />
                <span>Vaginorektální stěr</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.urogenital_sti.mop_two_slides`)} />
                <span>MOP — 2 podložní sklíčka</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.urogenital_sti.gonorrhea_smear`)} />
                <span>Nátěr na kapavku</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.urogenital_sti.trichomonas_smear`)} />
                <span>Nátěr na trichomonády</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.urogenital_sti.iud`)} />
                <span>IUD (nitroděložní tělísko)</span>
              </CheckRow>
            </Items>
          </SectionBlock>

          <SectionBlock>
            <SectionTitle>Odběr z lézí</SectionTitle>
            <Items>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.lesion_sample.anaerobic`)} />
                <span>Anaerobní kultivace</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.lesion_sample.leg_ulcer_swab`)} />
                <span>Stěr bércového vředu</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.lesion_sample.surgical_wound_swab`)} />
                <span>Stěr operační rány</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.lesion_sample.traumatic_wound_swab`)} />
                <span>Stěr traumatické rány</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.lesion_sample.fistula_swab`)} />
                <span>Stěr píštěle</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.lesion_sample.skin_swab`)} />
                <span>Kožní stěr</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.lesion_sample.decubitus_swab`)} />
                <span>Stěr dekubitu</span>
              </CheckRow>
            </Items>
          </SectionBlock>

          <SectionBlock>
            <SectionTitle>Moč</SectionTitle>
            <Items>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.urine.midstream`)} />
                <span>Střední proud</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.urine.nephrostomy_epicystostomy`)} />
                <span>Nefrostomie / epicystostomie</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.urine.permanent_catheter`)} />
                <span>Permanentní katétr</span>
              </CheckRow>
            </Items>
          </SectionBlock>

          <SectionBlock>
            <SectionTitle>CNS</SectionTitle>
            <Items>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.cns.csf`)} />
                <span>Mozkomíšní mok (CSF)</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.cns.streptococcus_pneumoniae_antigen`)} />
                <span>Streptococcus pneumoniae antigen</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.cns.aspergillus_antigen`)} />
                <span>Aspergillus antigen</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.cns.cryptococcus_antigen`)} />
                <span>Cryptococcus antigen</span>
              </CheckRow>
            </Items>
          </SectionBlock>

          <SectionBlock>
            <SectionTitle>Ucho</SectionTitle>
            <SelectRow>
              <SelectLabel>Strana:</SelectLabel>
              <StyledSelect {...register(`${p}.ear.side`)}>
                <option value="">— nevybráno —</option>
                <option value="L">Levé (L)</option>
                <option value="R">Pravé (R)</option>
                <option value="both">Obě</option>
              </StyledSelect>
            </SelectRow>
            <Items>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.ear.external_auditory_canal_swab`)} />
                <span>Stěr zevního zvukovodu</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.ear.middle_ear_content`)} />
                <span>Sekret středního ucha</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.ear.other.enabled`)} />
                <span>Jiné</span>
              </CheckRow>
              {earOtherEnabled && (
                <>
                  <SubInput
                    placeholder="Upřesnění"
                    {...register(`${p}.ear.other.text`, {
                      validate: (v) => !earOtherEnabled || !!v?.trim() || 'Upřesnění je povinné'
                    })}
                  />
                  <FieldError error={e?.form_data?.ear?.other?.text} />
                </>
              )}
            </Items>
          </SectionBlock>

          <SectionBlock>
            <SectionTitle>Oko</SectionTitle>
            <SelectRow>
              <SelectLabel>Strana:</SelectLabel>
              <StyledSelect {...register(`${p}.eye.side`)}>
                <option value="">— nevybráno —</option>
                <option value="L">Levé (L)</option>
                <option value="R">Pravé (R)</option>
                <option value="both">Obě</option>
              </StyledSelect>
            </SelectRow>
            <Items>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.eye.conjunctival_swab`)} />
                <span>Spojivkový stěr</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.eye.other.enabled`)} />
                <span>Jiné</span>
              </CheckRow>
              {eyeOtherEnabled && (
                <>
                  <SubInput
                    placeholder="Upřesnění"
                    {...register(`${p}.eye.other.text`, {
                      validate: (v) => !eyeOtherEnabled || !!v?.trim() || 'Upřesnění je povinné'
                    })}
                  />
                  <FieldError error={e?.form_data?.eye?.other?.text} />
                </>
              )}
            </Items>
          </SectionBlock>
        </Column>

        {/* ============ COLUMN 3 ============ */}
        <Column>
          <SectionBlock>
            <SectionTitle>Neonatologie</SectionTitle>
            <Items>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.neonatology.amniotic_fluid`)} />
                <span>Plodová voda</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.neonatology.ear_swab`)} />
                <span>Ušní stěr</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.neonatology.umbilicus_swab`)} />
                <span>Stěr pupku</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.neonatology.axilla_swab`)} />
                <span>Stěr axily</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.neonatology.gastric_content`)} />
                <span>Žaludeční obsah</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.neonatology.other.enabled`)} />
                <span>Jiné</span>
              </CheckRow>
              {neoOtherEnabled && (
                <>
                  <SubInput
                    placeholder="Upřesnění"
                    {...register(`${p}.neonatology.other.text`, {
                      validate: (v) => !neoOtherEnabled || !!v?.trim() || 'Upřesnění je povinné'
                    })}
                  />
                  <FieldError error={e?.form_data?.neonatology?.other?.text} />
                </>
              )}
            </Items>
          </SectionBlock>

          <SectionBlock>
            <SectionTitle>Pitevní vzorky</SectionTitle>
            <Items>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.pathology.liver`)} />
                <span>Játra</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.pathology.stomach`)} />
                <span>Žaludek</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.pathology.spleen`)} />
                <span>Slezina</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.pathology.lung`)} />
                <span>Plíce</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.pathology.small_intestine`)} />
                <span>Tenké střevo</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.pathology.lymph_node`)} />
                <span>Lymfatická uzlina</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.pathology.large_intestine`)} />
                <span>Tlusté střevo</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.pathology.other.enabled`)} />
                <span>Jiné</span>
              </CheckRow>
              {pathOtherEnabled && (
                <>
                  <SubInput
                    placeholder="Upřesnění"
                    {...register(`${p}.pathology.other.text`, {
                      validate: (v) => !pathOtherEnabled || !!v?.trim() || 'Upřesnění je povinné'
                    })}
                  />
                  <FieldError error={e?.form_data?.pathology?.other?.text} />
                </>
              )}
            </Items>
          </SectionBlock>

          <SectionBlock>
            <SectionTitle>Sterilita</SectionTitle>
            <Items>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.sterility.environment_swab`)} />
                <span>Stěr z prostředí</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.sterility.fluid`)} />
                <span>Tekutina</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.sterility.other.enabled`)} />
                <span>Jiné</span>
              </CheckRow>
              {sterOtherEnabled && (
                <>
                  <SubInput
                    placeholder="Upřesnění"
                    {...register(`${p}.sterility.other.text`, {
                      validate: (v) => !sterOtherEnabled || !!v?.trim() || 'Upřesnění je povinné'
                    })}
                  />
                  <FieldError error={e?.form_data?.sterility?.other?.text} />
                </>
              )}
            </Items>
          </SectionBlock>

          <SectionBlock>
            <SectionTitle>Cizí materiál</SectionTitle>
            <Items>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.foreign_material.drain`)} />
                <span>Drén</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.foreign_material.cannula`)} />
                <span>Kanyla</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.foreign_material.redon`)} />
                <span>Redon</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.foreign_material.vascular_catheter`)} />
                <span>Cévní katétr</span>
              </CheckRow>
            </Items>
          </SectionBlock>

          <SectionBlock>
            <SectionTitle>Kultivace na TBC</SectionTitle>
            <Items>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.tbc_cultivation.enabled`)} />
                <span>Provést kultivaci na TBC</span>
              </CheckRow>
              {tbcEnabled && (
                <>
                  <SubInput
                    placeholder="Materiál"
                    {...register(`${p}.tbc_cultivation.material`, {
                      validate: (v) => !tbcEnabled || !!v?.trim() || 'Materiál je povinný'
                    })}
                  />
                  <FieldError error={e?.form_data?.tbc_cultivation?.material} />
                </>
              )}
            </Items>
          </SectionBlock>

          <SectionBlock>
            <SectionTitle>Screening MRSA</SectionTitle>
            <Items>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.mrsa_screening.throat`)} />
                <span>Krk</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.mrsa_screening.nose`)} />
                <span>Nos</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.mrsa_screening.urine`)} />
                <span>Moč</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.mrsa_screening.hairline`)} />
                <span>Vlasová linie</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.mrsa_screening.perineum`)} />
                <span>Perineum</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.mrsa_screening.wound`)} />
                <span>Rána</span>
              </CheckRow>
            </Items>
          </SectionBlock>

          <SectionBlock>
            <SectionTitle>Krevní proud / BACTEC</SectionTitle>
            <SelectRow>
              <SelectLabel>Zdroj odběru:</SelectLabel>
              <StyledSelect {...register(`${p}.bloodstream_bactec.collection_source`)}>
                <option value="">— nevybráno —</option>
                <option value="peripheral_blood">Periferní krev</option>
                <option value="central_blood">Centrální krev</option>
                <option value="other_sterile">Jiný sterilní materiál</option>
              </StyledSelect>
            </SelectRow>
            {bactecSource === 'other_sterile' && (
              <>
                <SubInput
                  placeholder="Upřesnění jiného sterilního materiálu"
                  {...register(`${p}.bloodstream_bactec.other_sterile_text`, {
                    validate: (v) =>
                      bactecSource !== 'other_sterile' || !!v?.trim() || 'Upřesnění je povinné'
                  })}
                />
                <FieldError error={e?.form_data?.bloodstream_bactec?.other_sterile_text} />
              </>
            )}
            <BottleCounts>
              <BottleLabel>Počty lahviček:</BottleLabel>
              <BottleGrid>
                <span>Aerobní</span>
                <BottleInput
                  type="number"
                  min={0}
                  {...register(`${p}.bloodstream_bactec.bottle_counts.aerobic`, {
                    min: { value: 0, message: 'Minimálně 0' },
                    valueAsNumber: true
                  })}
                />
                <span>Anaerobní</span>
                <BottleInput
                  type="number"
                  min={0}
                  {...register(`${p}.bloodstream_bactec.bottle_counts.anaerobic`, {
                    min: { value: 0, message: 'Minimálně 0' },
                    valueAsNumber: true
                  })}
                />
                <span>Pediatrická</span>
                <BottleInput
                  type="number"
                  min={0}
                  {...register(`${p}.bloodstream_bactec.bottle_counts.pediatric`, {
                    min: { value: 0, message: 'Minimálně 0' },
                    valueAsNumber: true
                  })}
                />
              </BottleGrid>
            </BottleCounts>
            <Items>
              <CheckRow>
                <input
                  type="checkbox"
                  {...register(`${p}.bloodstream_bactec.extended_cultivation_hacek`)}
                />
                <span>Rozšířená kultivace HACEK</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.bloodstream_bactec.sequencing`)} />
                <span>Sekvenování</span>
              </CheckRow>
              <CheckRow>
                <input
                  type="checkbox"
                  {...register(`${p}.bloodstream_bactec.other_request.enabled`)}
                />
                <span>Jiná žádost</span>
              </CheckRow>
              {bactecOtherEnabled && (
                <>
                  <SubInput
                    placeholder="Upřesnění"
                    {...register(`${p}.bloodstream_bactec.other_request.text`, {
                      validate: (v) => !bactecOtherEnabled || !!v?.trim() || 'Upřesnění je povinné'
                    })}
                  />
                  <FieldError error={e?.form_data?.bloodstream_bactec?.other_request?.text} />
                </>
              )}
            </Items>
          </SectionBlock>

          <SectionBlock>
            <SectionTitle>Mykologie</SectionTitle>
            <Items>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.mycology.cultivation.enabled`)} />
                <span>Kultivace — materiál:</span>
              </CheckRow>
              {mycoEnabled && (
                <>
                  <SubInput
                    placeholder="Materiál"
                    {...register(`${p}.mycology.cultivation.material`, {
                      validate: (v) => !mycoEnabled || !!v?.trim() || 'Materiál je povinný'
                    })}
                  />
                  <FieldError error={e?.form_data?.mycology?.cultivation?.material} />
                </>
              )}
              <CheckRow>
                <input type="checkbox" {...register(`${p}.mycology.panfungal_antigen_bd_glucan`)} />
                <span>Panfungální antigen — BD-glucan</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.mycology.galactomannan_aspergillus`)} />
                <span>Galaktomannan — Aspergillus</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.mycology.cryptococcus_antigen`)} />
                <span>Cryptococcus antigen</span>
              </CheckRow>
              <CheckRow>
                <input type="checkbox" {...register(`${p}.mycology.pcr_multiplex_dermatophyta`)} />
                <span>PCR multiplex — Dermatofyta</span>
              </CheckRow>
            </Items>
          </SectionBlock>
        </Column>
      </MainGrid>

      {/* ============ PCR ============ */}
      <Hr />
      <PcrTitle>PCR</PcrTitle>
      <PcrGrid>
        <SectionBlock>
          <SectionTitle>Herpetické viry</SectionTitle>
          <Items>
            <CheckRow>
              <input type="checkbox" {...register(`${p}.pcr.herpetic.hsv1_hsv2`)} />
              <span>HSV-1 / HSV-2</span>
            </CheckRow>
            <CheckRow>
              <input type="checkbox" {...register(`${p}.pcr.herpetic.hsv1_hsv2_vzv`)} />
              <span>HSV-1 / HSV-2 / VZV</span>
            </CheckRow>
            <CheckRow>
              <input type="checkbox" {...register(`${p}.pcr.herpetic.cmv_quantitative`)} />
              <span>CMV — kvantitativní</span>
            </CheckRow>
            <CheckRow>
              <input type="checkbox" {...register(`${p}.pcr.herpetic.ebv_quantitative`)} />
              <span>EBV — kvantitativní</span>
            </CheckRow>
            <CheckRow>
              <input type="checkbox" {...register(`${p}.pcr.herpetic.hhv6_7`)} />
              <span>HHV-6 / HHV-7</span>
            </CheckRow>
            <CheckRow>
              <input type="checkbox" {...register(`${p}.pcr.herpetic.hhv8`)} />
              <span>HHV-8</span>
            </CheckRow>
          </Items>
        </SectionBlock>

        <SectionBlock>
          <SectionTitle>Respirační</SectionTitle>
          <Items>
            <CheckRow>
              <input type="checkbox" {...register(`${p}.pcr.respiratory.sars_cov2_flu_rsv`)} />
              <span>SARS-CoV-2 / Flu / RSV</span>
            </CheckRow>
            <CheckRow>
              <input type="checkbox" {...register(`${p}.pcr.respiratory.sars_cov2_flu_rsv_gxp`)} />
              <span>SARS-CoV-2 / Flu / RSV (GXP)</span>
            </CheckRow>
            <CheckRow>
              <input type="checkbox" {...register(`${p}.pcr.respiratory.multiplex_cap`)} />
              <span>Multiplex CAP</span>
            </CheckRow>
            <CheckRow>
              <input type="checkbox" {...register(`${p}.pcr.respiratory.multiplex_resp_panel`)} />
              <span>Multiplex respirační panel</span>
            </CheckRow>
            <CheckRow>
              <input
                type="checkbox"
                {...register(`${p}.pcr.respiratory.multiplex_pneumonia_plus`)}
              />
              <span>Multiplex Pneumonia Plus</span>
            </CheckRow>
            <CheckRow>
              <input
                type="checkbox"
                {...register(`${p}.pcr.respiratory.mycobacterium_tbc_complex`)}
              />
              <span>Mycobacterium TBC complex</span>
            </CheckRow>
            <CheckRow>
              <input
                type="checkbox"
                {...register(`${p}.pcr.respiratory.mycobacterium_tbc_complex_gxp`)}
              />
              <span>Mycobacterium TBC complex (GXP)</span>
            </CheckRow>
            <CheckRow>
              <input type="checkbox" {...register(`${p}.pcr.respiratory.atypical_mycobacteria`)} />
              <span>Atypické mykobakterie</span>
            </CheckRow>
            <CheckRow>
              <input type="checkbox" {...register(`${p}.pcr.respiratory.strain_identification`)} />
              <span>Identifikace kmene</span>
            </CheckRow>
            <CheckRow>
              <input type="checkbox" {...register(`${p}.pcr.respiratory.aspergillus_spp`)} />
              <span>Aspergillus spp.</span>
            </CheckRow>
            <CheckRow>
              <input type="checkbox" {...register(`${p}.pcr.respiratory.pneumocystis_jirovecii`)} />
              <span>Pneumocystis jirovecii</span>
            </CheckRow>
          </Items>
        </SectionBlock>

        <SectionBlock>
          <SectionTitle>Pediatrické exantematózy</SectionTitle>
          <Items>
            <CheckRow>
              <input
                type="checkbox"
                {...register(`${p}.pcr.pediatric_exanthematic.parvovirus_b19`)}
              />
              <span>Parvovirus B19</span>
            </CheckRow>
            <CheckRow>
              <input type="checkbox" {...register(`${p}.pcr.pediatric_exanthematic.parotitis`)} />
              <span>Příušnice</span>
            </CheckRow>
            <CheckRow>
              <input type="checkbox" {...register(`${p}.pcr.pediatric_exanthematic.morbilli`)} />
              <span>Spalničky</span>
            </CheckRow>
          </Items>
        </SectionBlock>

        <SectionBlock>
          <SectionTitle>Virové hepatitidy</SectionTitle>
          <Items>
            <CheckRow>
              <input type="checkbox" {...register(`${p}.pcr.viral_hepatitides.hbv_quantitative`)} />
              <span>HBV — kvantitativní</span>
            </CheckRow>
            <CheckRow>
              <input type="checkbox" {...register(`${p}.pcr.viral_hepatitides.hcv_quantitative`)} />
              <span>HCV — kvantitativní</span>
            </CheckRow>
            <CheckRow>
              <input type="checkbox" {...register(`${p}.pcr.viral_hepatitides.hcv_genotyping`)} />
              <span>HCV — genotypizace</span>
            </CheckRow>
            <CheckRow>
              <input type="checkbox" {...register(`${p}.pcr.viral_hepatitides.hev`)} />
              <span>HEV</span>
            </CheckRow>
          </Items>
        </SectionBlock>

        <SectionBlock>
          <SectionTitle>STI</SectionTitle>
          <Items>
            <CheckRow>
              <input type="checkbox" {...register(`${p}.pcr.sti.chlamydia_trachomatis`)} />
              <span>Chlamydia trachomatis</span>
            </CheckRow>
            <CheckRow>
              <input type="checkbox" {...register(`${p}.pcr.sti.chlamydia_trachomatis_gxp`)} />
              <span>Chlamydia trachomatis (GXP)</span>
            </CheckRow>
            <CheckRow>
              <input type="checkbox" {...register(`${p}.pcr.sti.neisseria_gonorrhoeae`)} />
              <span>Neisseria gonorrhoeae</span>
            </CheckRow>
            <CheckRow>
              <input type="checkbox" {...register(`${p}.pcr.sti.neisseria_gonorrhoeae_gxp`)} />
              <span>Neisseria gonorrhoeae (GXP)</span>
            </CheckRow>
            <CheckRow>
              <input type="checkbox" {...register(`${p}.pcr.sti.monkeypox`)} />
              <span>Opičí neštovice</span>
            </CheckRow>
            <CheckRow>
              <input type="checkbox" {...register(`${p}.pcr.sti.multiplex_panel_sti`)} />
              <span>Multiplex panel STI</span>
            </CheckRow>
            <CheckRow>
              <input type="checkbox" {...register(`${p}.pcr.sti.multiplex_panel_gu`)} />
              <span>Multiplex panel GU</span>
            </CheckRow>
          </Items>
        </SectionBlock>

        <SectionBlock>
          <SectionTitle>Neuroinfekce</SectionTitle>
          <Items>
            <CheckRow>
              <input type="checkbox" {...register(`${p}.pcr.neuroinfections.enteroviruses`)} />
              <span>Enteroviry</span>
            </CheckRow>
            <CheckRow>
              <input type="checkbox" {...register(`${p}.pcr.neuroinfections.borrelia`)} />
              <span>Borrelia</span>
            </CheckRow>
            <CheckRow>
              <input
                type="checkbox"
                {...register(`${p}.pcr.neuroinfections.multiplex_meningitis_viruses`)}
              />
              <span>Multiplex meningitida — viry</span>
            </CheckRow>
            <CheckRow>
              <input
                type="checkbox"
                {...register(`${p}.pcr.neuroinfections.multiplex_meningitis_bacteria`)}
              />
              <span>Multiplex meningitida — bakterie</span>
            </CheckRow>
            <CheckRow>
              <input
                type="checkbox"
                {...register(`${p}.pcr.neuroinfections.multiplex_meningitis`)}
              />
              <span>Multiplex meningitida</span>
            </CheckRow>
          </Items>
        </SectionBlock>

        <SectionBlock>
          <SectionTitle>Klouby / implantáty</SectionTitle>
          <Items>
            <CheckRow>
              <input type="checkbox" {...register(`${p}.pcr.joint_implants.multiplex_joints`)} />
              <span>Multiplex klouby</span>
            </CheckRow>
          </Items>
        </SectionBlock>

        <SectionBlock>
          <SectionTitle>Cestovatelské infekce</SectionTitle>
          <Items>
            <CheckRow>
              <input
                type="checkbox"
                {...register(`${p}.pcr.travel_infections.multiplex_tropical_fevers`)}
              />
              <span>Multiplex tropické horečky</span>
            </CheckRow>
            <CheckRow>
              <input type="checkbox" {...register(`${p}.pcr.travel_infections.malaria`)} />
              <span>Malárie</span>
            </CheckRow>
            <CheckRow>
              <input
                type="checkbox"
                {...register(`${p}.pcr.travel_infections.multiplex_protozoa`)}
              />
              <span>Multiplex prvoky</span>
            </CheckRow>
            <CheckRow>
              <input
                type="checkbox"
                {...register(`${p}.pcr.travel_infections.multiplex_helminths`)}
              />
              <span>Multiplex helminty</span>
            </CheckRow>
          </Items>
        </SectionBlock>

        <SectionBlock>
          <SectionTitle>Ostatní PCR</SectionTitle>
          <Items>
            <CheckRow>
              <input type="checkbox" {...register(`${p}.pcr.other.multiplex_tick_borne`)} />
              <span>Multiplex klíšťová onemocnění</span>
            </CheckRow>
            <CheckRow>
              <input type="checkbox" {...register(`${p}.pcr.other.multiplex_dermatophyta`)} />
              <span>Multiplex Dermatofyta</span>
            </CheckRow>
            <CheckRow>
              <input type="checkbox" {...register(`${p}.pcr.other.pcr_tularemia`)} />
              <span>PCR tularémie</span>
            </CheckRow>
          </Items>
        </SectionBlock>
      </PcrGrid>

      {/* ============ POZNÁMKA ============ */}
      <Hr />
      <NoteRow>
        <NoteLabel>Poznámka pro laboratoř:</NoteLabel>
        <NoteTextarea placeholder="Volný text pro laboratoř" {...register('microbiology.note')} />
      </NoteRow>
    </Wrapper>
  );
};

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing(4)};
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(260px, 1fr));
  gap: ${Spacing(4)};

  @media (max-width: 1400px) {
    grid-template-columns: repeat(2, minmax(260px, 1fr));
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing(4)};
`;

const SectionBlock = styled.section``;

const SectionTitle = styled.div`
  font-weight: 700;
  margin-bottom: ${Spacing(2)};
  color: ${Colors.mainText};
`;

const Items = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing(1)};
`;

const CheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  user-select: none;
  cursor: pointer;

  input[type='checkbox'] {
    width: 15px;
    height: 15px;
    flex-shrink: 0;
  }

  span {
    color: ${Colors.grey};
    font-size: 13px;
  }
`;

const SubInput = styled.input`
  width: 100%;
  height: 30px;
  border: 1px solid ${Colors.lightGrey};
  padding: 0 ${Spacing(2)};
  font-size: 13px;
  margin-top: 2px;
`;

const SelectRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing(2)};
  margin-bottom: ${Spacing(2)};
`;

const SelectLabel = styled.span`
  font-size: 13px;
  color: ${Colors.grey};
  white-space: nowrap;
`;

const StyledSelect = styled.select`
  height: 30px;
  border: 1px solid ${Colors.lightGrey};
  padding: 0 ${Spacing(1)};
  font-size: 13px;
  flex: 1;
`;

const BottleCounts = styled.div`
  margin: ${Spacing(2)} 0;
`;

const BottleLabel = styled.div`
  font-size: 13px;
  color: ${Colors.grey};
  margin-bottom: ${Spacing(1)};
`;

const BottleGrid = styled.div`
  display: grid;
  grid-template-columns: auto 60px;
  gap: ${Spacing(1)} ${Spacing(2)};
  align-items: center;

  span {
    font-size: 13px;
    color: ${Colors.grey};
  }
`;

const BottleInput = styled.input`
  width: 60px;
  height: 28px;
  border: 1px solid ${Colors.lightGrey};
  padding: 0 ${Spacing(1)};
  font-size: 13px;
  text-align: right;
`;

const Hr = styled.hr`
  border: none;
  border-top: 1px solid ${Colors.lightGrey};
  margin: 0;
`;

const PcrTitle = styled.div`
  font-weight: 700;
  font-size: 15px;
  color: ${Colors.mainText};
`;

const PcrGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(220px, 1fr));
  gap: ${Spacing(4)};

  @media (max-width: 1400px) {
    grid-template-columns: repeat(2, minmax(220px, 1fr));
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const NoteRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing(2)};
`;

const NoteLabel = styled.div`
  font-weight: 700;
  color: ${Colors.grey};
`;

const NoteTextarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  border: 1px solid ${Colors.lightGrey};
  padding: ${Spacing(2)};
  font-size: 13px;
  resize: vertical;
`;
